/**
 * DeliStore (the Woo plugin) ↔ DeliCRM, server to server (spec §7.1, plan 06).
 *
 * Plugin → CRM: every request carries a timestamp and an HMAC-SHA256 signature over
 * `<timestamp>.<METHOD>.<path with query>.<raw body>` with the shared store secret (a Settings
 * value). Only routes marked for the store accept it; everything else stays staff-only.
 *
 * CRM → plugin: the events the store needs (prices, stock, store order statuses) go to the store
 * webhook through the event outbox, signed like n8n deliveries (`docs/events.md`).
 */
export const STORE_HEADERS = {
  timestamp: 'x-deli-store-timestamp',
  signature: 'x-deli-store-signature',
} as const;

/** How far a signed request's timestamp may be from the CRM's clock. */
export const STORE_SIGNATURE_TOLERANCE_SECONDS = 300;

/** The actor id written to audit rows, quotes and orders the store creates. */
export const STORE_ACTOR_ID = '0de15703-e000-4000-8000-000000000000';
export const STORE_ACTOR_NAME = 'DeliStore';

/**
 * Features this CRM build offers the store (`GET /api/v1/meta` → `capabilities`). The plugin
 * enables a feature only when its toggle is on **and** the CRM lists it.
 */
export const STORE_CAPABILITIES = [
  'catalogue_sync', // PUT /catalogue/products/{sku}
  'price_lists', // GET /pricing/price-list, /pricing/customer-prices/{id}
  'quotes', // POST /pricing/quote
  'customers_link', // POST /customers/link
  'orders_cod', // POST /orders/from-woo, cash on delivery
  'stock_events', // stock.changed to the store webhook
  'order_status_events', // order.status_changed (store orders) to the store webhook
  'loyalty', // Q-points: quote pointsRequested / pointsToEarn, GET /loyalty/customers/{id}/summary
  'orders_charged', // POST /orders/from-woo/charged: orders sent after payment (spec §7.7)
] as const;
export type StoreCapability = (typeof STORE_CAPABILITIES)[number];
